import Honeybadger from '@honeybadger-io/js';
import PropTypes from 'prop-types';
import React, { useState, useEffect, useRef } from 'react';

import ActionButtons from '../../components/sections/provisional-offer-options/ActionButtons';
import FullOfferDetails from '../../components/sections/provisional-offer-options/FullOfferDetails';
import LoanOffer from '../../components/sections/provisional-offer-options/LoanOffer';
import OfferDetails from '../../components/sections/provisional-offer-options/OfferDetails';
import OfferTermsAndConditions from '../../components/sections/provisional-offer-options/OfferTermsAndConditions';
import WhiteCard from '../../components/WhiteCard';
import {
  PROVISIONAL_OFFER_CUSTOMISE,
  PROVISIONAL_OFFER_DETAILED_OFFER,
  PROVISIONAL_OFFER_LOAN_TYPE_SELECTION,
} from '../../constants/heapEvents';
import { optionsShown, track } from '../../lib/heapHelpers';
import { scrollToTermsAndConditions } from '../../lib/provisionalOfferHelper';

import './index.scss';

const PROMO_START_DATE = new Date('2024-04-23T00:00:00Z');
const PROMO_END_DATE = new Date('2024-05-21T23:59:59Z');

const ProvisionalOfferOptionsPage = ({
  data,
  isProvisionalOfferAccepted,
  acceptOffer,
  rejectOffer,
  selectedOfferOption,
  onSelectedOfferChange,
  handleCosignerDetails,
}) => {
  const customiseLoanComponentRef = useRef(null);
  const scrollToCustomiseLoanComponent = () => {
    if (customiseLoanComponentRef && customiseLoanComponentRef.current) {
      customiseLoanComponentRef.current.scrollIntoView({ behavior: 'smooth' });
      track(PROVISIONAL_OFFER_CUSTOMISE, {
        'Application GUID': data.id,
        'Customer GUID': data.customer_guid,
        'Options shown': optionsShown(data.offer_options),
      });
    }
  };

  const [isTermsAndConditionsAccepted, setIsTermsAndConditionsAccepted] = useState(false);

  const [selectedOffer, setSelectedOffer] = useState(() => {
    if (isProvisionalOfferAccepted) {
      const offer = data.offer_options.find(
        option =>
          // eslint-disable-next-line react/prop-types
          option.loan_type === data.accepted_offer_details.loan_type &&
          // eslint-disable-next-line react/prop-types
          option.term_in_months === data.accepted_offer_details.term_in_months,
      );

      if (!offer) {
        Honeybadger.notify('No matching offer found for accepted provisional offer', {
          context: {
            accepted_offer_details: data.accepted_offer_details,
            offer_options: data.offer_options,
          },
        });
      }
      return offer || {};
    } else {
      const cosignerOffer = data.offer_options.find(
        option => option.loan_type === 'cosigner_inschool',
      );
      return cosignerOffer || data.offer_options[0] || selectedOfferOption || {};
    }
  });

  const offerCreatedAt = new Date(data.offer_options[0].created_at);
  const offerIssuedDuringPromoPeriod =
    offerCreatedAt >= PROMO_START_DATE && offerCreatedAt <= PROMO_END_DATE;

  const offerOptions = data.offer_options;

  const [showReminder, setShowReminder] = useState(false);
  const hasCosigner =
    data.offer_options.filter(option => option.loan_type === 'cosigner_inschool').length > 0;
  const [cosignerSelection, setCosignerSelection] = useState(() => {
    if (isProvisionalOfferAccepted) {
      return selectedOffer && selectedOffer.loan_type === 'cosigner_inschool'
        ? 'cosigner'
        : 'alone';
    } else {
      return hasCosigner ? 'cosigner' : 'alone';
    }
  });
  const [showDetails, setShowDetails] = useState(isProvisionalOfferAccepted);

  const handleAcceptTermsAndConditions = accepted => {
    setIsTermsAndConditionsAccepted(accepted);
    setShowReminder(!accepted);
  };

  const handleAcceptClick = () => {
    if (!isTermsAndConditionsAccepted) {
      setShowReminder(true);
      scrollToTermsAndConditions();
    }
  };

  const updateCosignerSelection = selection => {
    if (selection !== cosignerSelection) {
      setCosignerSelection(selection);
      track(PROVISIONAL_OFFER_LOAN_TYPE_SELECTION, {
        'Application GUID': data.id,
        'Customer GUID': data.customer_guid,
        'Cosigner selection': selection === 'cosigner' ? 'Cosigner' : 'On my own',
        'Options shown': optionsShown(data.offer_options),
      });
      const filteredOffers = data.offer_options.filter(option =>
        selection === 'cosigner'
          ? option.loan_type === 'cosigner_inschool'
          : option.loan_type === 'standard',
      );
      if (filteredOffers.length > 0) {
        updateSelectedOffer(filteredOffers[0]);
      }
    }
  };

  const updateSelectedOffer = offer => {
    setSelectedOffer(offer);
    onSelectedOfferChange(offer);
  };

  useEffect(() => {
    // we need to reset the base font-size so that we can use rem units safely
    document.documentElement.style.fontSize = '16px';

    return () => {
      document.documentElement.style.fontSize = null;
    };
  });

  const handleShowDetailsClick = () => {
    setShowDetails(true);
    track(PROVISIONAL_OFFER_DETAILED_OFFER, {
      'Application GUID': data.id,
      'Customer GUID': data.customer_guid,
      'Options shown': optionsShown(data.offer_options),
    });
  };

  return (
    <div className="provisional-offer-options">
      <div className="provisional-offer-options__content">
        <WhiteCard>
          <div className="provisional-offer-options__content-header">
            <LoanOffer data={data} selectedOffer={selectedOffer} />
          </div>
          <div className="provisional-offer-options__content-details">
            <OfferDetails
              data={data}
              offerAccepted={isProvisionalOfferAccepted}
              offerOptions={offerOptions}
              selectedOffer={selectedOffer}
              onSelectedOfferChange={updateSelectedOffer}
              offerIssuedDuringPromoPeriod={offerIssuedDuringPromoPeriod}
              hasCosigner={hasCosigner}
              cosignerSelection={cosignerSelection}
              onCosignerSelectionChange={updateCosignerSelection}
              showDetails={showDetails}
              onShowDetailsClick={handleShowDetailsClick}
              customiseLoanComponentRef={customiseLoanComponentRef}
            />
          </div>
          {(showDetails || isProvisionalOfferAccepted) && (
            <div className="provisional-offer-options__content-full-offer-details">
              <FullOfferDetails
                currency={data.loan_currency}
                initialPaymentDate={data.initial_payment_date}
                firstRepaymentDate={data.first_repayment_date}
                loanAmount={data.loan_amount}
                adminFee={data.admin_fee}
                adminFeePercentage={data.admin_fee_percentage}
                selectedOffer={selectedOffer}
                processingFeeValue={data.processing_fee_value}
                cosignerSelection={cosignerSelection}
                applicationID={data.id}
                expiryDate={data.expiry_date}
              />
              <OfferTermsAndConditions
                alreadyAccepted={isProvisionalOfferAccepted}
                showReminder={showReminder}
                onAcceptTermsAndConditions={handleAcceptTermsAndConditions}
                applicationId={data.id}
                customerGuid={data.customer_guid}
                offerIssuedDuringPromoPeriod={offerIssuedDuringPromoPeriod}
              />
            </div>
          )}
        </WhiteCard>
        {!isProvisionalOfferAccepted && showDetails && (
          <ActionButtons
            applicationId={data.id}
            customerGuid={data.customer_guid}
            isTermsAndConditionsAccepted={isTermsAndConditionsAccepted}
            onAcceptClick={handleAcceptClick}
            acceptOffer={acceptOffer}
            rejectOffer={rejectOffer}
            cosignerSelection={cosignerSelection}
            scrollToCustomiseLoanComponent={scrollToCustomiseLoanComponent}
            handleCosignerDetails={handleCosignerDetails}
            addAsFooter={true}
          />
        )}
      </div>
    </div>
  );
};

ProvisionalOfferOptionsPage.propTypes = {
  data: PropTypes.shape({
    accepted_offer_details: PropTypes.shape({
      accepted_at: PropTypes.string,
    }),
    offer_options: PropTypes.arrayOf(
      PropTypes.shape({
        created_at: PropTypes.string.isRequired,
      }),
    ).isRequired,
    step_down_offer_options: PropTypes.arrayOf(
      PropTypes.shape({
        created_at: PropTypes.string.isRequired,
      }),
    ).isRequired,
    id: PropTypes.string.isRequired,
    initial_payment_date: PropTypes.string,
    first_disbursement_date: PropTypes.string.isRequired,
    expiry_date: PropTypes.string,
    school_short_name: PropTypes.string.isRequired,
    university_name: PropTypes.string.isRequired,
    course_name: PropTypes.string.isRequired,
    period_start_date: PropTypes.string.isRequired,
    period_end_date: PropTypes.string.isRequired,
    loan_currency: PropTypes.string.isRequired,
    first_repayment_date: PropTypes.string.isRequired,
    loan_amount: PropTypes.string.isRequired,
    admin_fee: PropTypes.string.isRequired,
    admin_fee_percentage: PropTypes.string.isRequired,
    processing_fee_value: PropTypes.string.isRequired,
    customer_guid: PropTypes.string.isRequired,
  }).isRequired,
  isProvisionalOfferAccepted: PropTypes.bool.isRequired,
  acceptOffer: PropTypes.func.isRequired,
  rejectOffer: PropTypes.func.isRequired,
  selectedOfferOption: PropTypes.object,
  onSelectedOfferChange: PropTypes.func.isRequired,
  handleCosignerDetails: PropTypes.func.isRequired,
};

export default ProvisionalOfferOptionsPage;
